import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Chart } from 'primereact/chart';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { useAuth } from '../contexts/AuthContext';
// import { examsAPI, classesAPI, sectionsAPI, studentsAPI } from '../services/api';
import { 
  AcademicCapIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClockIcon,
  ChartBarIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { examsAPI, sectionsAPI } from '../services/api';
import { classesAPI } from '../services/classService';
import { studentsAPI } from '../services/studentService';

const Exams = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [exams, setExams] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Dialog states
  const [examDialog, setExamDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  
  // Form states
  const [examForm, setExamForm] = useState({
    name: '',
    description: '',
    examType: '',
    classId: null,
    sectionId: null,
    startDate: null,
    endDate: null,
    totalMarks: null,
    passingMarks: null,
    isActive: true
  });
  
  const [resultForm, setResultForm] = useState({
    studentId: null,
    marksObtained: null,
    grade: '',
    remarks: ''
  });
  
  const [editingExam, setEditingExam] = useState(null);

  const examTypes = [
    { label: 'Unit Test', value: 'unit_test' },
    { label: 'Mid Term', value: 'mid_term' },
    { label: 'Final Exam', value: 'final_exam' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Half Yearly', value: 'half_yearly' },
    { label: 'Annual', value: 'annual' }
  ];

  const gradeOptions = [
    { label: 'A+', value: 'A+' },
    { label: 'A', value: 'A' },
    { label: 'B+', value: 'B+' },
    { label: 'B', value: 'B' },
    { label: 'C+', value: 'C+' },
    { label: 'C', value: 'C' },
    { label: 'D', value: 'D' },
    { label: 'F', value: 'F' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (examForm.classId) {
      loadSections();
    }
  }, [examForm.classId]);

  useEffect(() => {
    if (examForm.classId && examForm.sectionId) {
      loadStudents();
    }
  }, [examForm.classId, examForm.sectionId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadExams(),
        loadClasses()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load exam data'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadExams = async () => {
    try {
      const response = await examsAPI.getAll(user.schoolId);
      setExams(response.data.data || []);
    } catch (error) {
      console.error('Error loading exams:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const response = await classesAPI.getAll(user.schoolId);
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadSections = async () => {
    try {
      const response = await classesAPI.getByClass(user.schoolId, examForm.classId);
      setSections(response.data.data || []);
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await studentsAPI.getAll(user.schoolId, {
        classId: examForm.classId,
        sectionId: examForm.sectionId
      });
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadExamResults = async (examId) => {
    try {
      const response = await examsAPI.getResults(user.schoolId, examId);
      setExamResults(response.data.data || []);
    } catch (error) {
      console.error('Error loading exam results:', error);
    }
  };

  const handleCreateExam = () => {
    setExamForm({
      name: '',
      description: '',
      examType: '',
      classId: null,
      sectionId: null,
      startDate: null,
      endDate: null,
      totalMarks: null,
      passingMarks: null,
      isActive: true
    });
    setEditingExam(null);
    setExamDialog(true);
  };

  const handleEditExam = (examData) => {
    setExamForm({
      name: examData.name,
      description: examData.description || '',
      examType: examData.exam_type,
      classId: examData.class_id,
      sectionId: examData.section_id,
      startDate: new Date(examData.start_date),
      endDate: new Date(examData.end_date),
      totalMarks: examData.total_marks,
      passingMarks: examData.passing_marks,
      isActive: examData.is_active
    });
    setEditingExam(examData);
    setExamDialog(true);
  };

  const handleSaveExam = async () => {
    try {
      setLoading(true);
      
      if (editingExam) {
        await examsAPI.update(user.schoolId, editingExam.id, examForm);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Exam updated successfully'
        });
      } else {
        await examsAPI.create(user.schoolId, examForm);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Exam created successfully'
        });
      }
      
      setExamDialog(false);
      await loadExams();
    } catch (error) {
      console.error('Error saving exam:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to save exam'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExam = (examData) => {
    confirmDialog({
      message: `Are you sure you want to delete exam "${examData.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await examsAPI.delete(user.schoolId, examData.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Exam deleted successfully'
          });
          await loadExams();
        } catch (error) {
          console.error('Error deleting exam:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to delete exam'
          });
        }
      }
    });
  };

  const handleViewResults = async (examData) => {
    setSelectedExam(examData);
    await loadExamResults(examData.id);
  };

  const handleAddResult = (examData) => {
    setSelectedExam(examData);
    setResultForm({
      studentId: null,
      marksObtained: null,
      grade: '',
      remarks: ''
    });
    setResultDialog(true);
  };

  const handleSaveResult = async () => {
    try {
      setLoading(true);
      await examsAPI.addResult(user.schoolId, selectedExam.id, resultForm);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Result added successfully'
      });
      
      setResultDialog(false);
      await loadExamResults(selectedExam.id);
    } catch (error) {
      console.error('Error adding result:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to add result'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      upcoming: { label: 'Upcoming', severity: 'info' },
      ongoing: { label: 'Ongoing', severity: 'warning' },
      completed: { label: 'Completed', severity: 'success' },
      cancelled: { label: 'Cancelled', severity: 'danger' }
    };
    
    const config = statusConfig[status] || { label: status, severity: 'secondary' };
    return <Badge value={config.label} severity={config.severity} />;
  };

  const getGradeBadge = (grade) => {
    const gradeColors = {
      'A+': 'success',
      'A': 'success',
      'B+': 'info',
      'B': 'info',
      'C+': 'warning',
      'C': 'warning',
      'D': 'danger',
      'F': 'danger'
    };
    
    return <Badge value={grade} severity={gradeColors[grade] || 'secondary'} />;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex space-x-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-info"
          onClick={() => handleViewResults(rowData)}
          tooltip="View Results"
        />
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-text p-button-success"
          onClick={() => handleAddResult(rowData)}
          tooltip="Add Result"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-warning"
          onClick={() => handleEditExam(rowData)}
          tooltip="Edit Exam"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDeleteExam(rowData)}
          tooltip="Delete Exam"
        />
      </div>
    );
  };

  const marksBodyTemplate = (rowData) => {
    return `${rowData.marks_obtained}/${rowData.total_marks}`;
  };

  const percentageBodyTemplate = (rowData) => {
    const percentage = ((rowData.marks_obtained / rowData.total_marks) * 100).toFixed(1);
    return `${percentage}%`;
  };

  const leftToolbarTemplate = () => {
    return (
      <div className="flex space-x-2">
        <Button
          label="New Exam"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleCreateExam}
        />
      </div>
    );
  };

  const getPerformanceChartData = () => {
    if (!examResults.length) return null;

    const gradeDistribution = examResults.reduce((acc, result) => {
      acc[result.grade] = (acc[result.grade] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(gradeDistribution),
      datasets: [
        {
          data: Object.values(gradeDistribution),
          backgroundColor: [
            '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#6B7280'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Grade Distribution'
      }
    }
  };

  const getExamStats = () => {
    const totalExams = exams.length;
    const upcomingExams = exams.filter(exam => new Date(exam.start_date) > new Date()).length;
    const completedExams = exams.filter(exam => new Date(exam.end_date) < new Date()).length;
    const ongoingExams = exams.filter(exam => {
      const now = new Date();
      return new Date(exam.start_date) <= now && new Date(exam.end_date) >= now;
    }).length;

    return { totalExams, upcomingExams, completedExams, ongoingExams };
  };

  const stats = getExamStats();

  return (
    <div className="p-6 space-y-6">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Exam Management</h1>
          <p className="text-gray-600 mt-1">Manage exams, schedules, and results</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Total Exams</p>
              <p className="text-3xl font-bold">{stats.totalExams}</p>
            </div>
            <AcademicCapIcon className="h-12 w-12 text-blue-200" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Completed</p>
              <p className="text-3xl font-bold">{stats.completedExams}</p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-200" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100">Ongoing</p>
              <p className="text-3xl font-bold">{stats.ongoingExams}</p>
            </div>
            <ClockIcon className="h-12 w-12 text-yellow-200" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Upcoming</p>
              <p className="text-3xl font-bold">{stats.upcomingExams}</p>
            </div>
            <CalendarIcon className="h-12 w-12 text-purple-200" />
          </div>
        </Card>
      </div>

      <TabView>
        <TabPanel header="All Exams" leftIcon="pi pi-list">
          <Card>
            <Toolbar className="mb-4" left={leftToolbarTemplate} />
            
            <DataTable
              value={exams}
              loading={loading}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              responsiveLayout="scroll"
              emptyMessage="No exams found"
            >
              <Column field="name" header="Exam Name" sortable />
              <Column field="exam_type" header="Type" />
              <Column field="class_name" header="Class" />
              <Column field="section_name" header="Section" />
              <Column 
                field="start_date" 
                header="Start Date"
                body={(rowData) => new Date(rowData.start_date).toLocaleDateString()}
                sortable
              />
              <Column 
                field="end_date" 
                header="End Date"
                body={(rowData) => new Date(rowData.end_date).toLocaleDateString()}
                sortable
              />
              <Column 
                field="total_marks" 
                header="Total Marks"
                sortable
              />
              <Column 
                field="passing_marks" 
                header="Passing Marks"
              />
              <Column 
                field="status" 
                header="Status"
                body={(rowData) => {
                  const now = new Date();
                  const startDate = new Date(rowData.start_date);
                  const endDate = new Date(rowData.end_date);
                  
                  let status = 'upcoming';
                  if (startDate <= now && endDate >= now) status = 'ongoing';
                  else if (endDate < now) status = 'completed';
                  
                  return getStatusBadge(status);
                }}
              />
              <Column body={actionBodyTemplate} header="Actions" />
            </DataTable>
          </Card>
        </TabPanel>

        <TabPanel header="Exam Results" leftIcon="pi pi-chart-bar">
          {selectedExam ? (
            <div className="space-y-6">
              <Card>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedExam.name}</h2>
                    <p className="text-gray-600 mt-1">
                      {selectedExam.class_name} - {selectedExam.section_name}
                    </p>
                    <div className="flex items-center space-x-4 mt-3">
                      <Badge value={`Total Marks: ${selectedExam.total_marks}`} severity="info" />
                      <Badge value={`Passing Marks: ${selectedExam.passing_marks}`} severity="warning" />
                      <Badge value={`${examResults.length} Results`} severity="success" />
                    </div>
                  </div>
                  <Button
                    label="Add Result"
                    icon="pi pi-plus"
                    className="p-button-success"
                    onClick={() => handleAddResult(selectedExam)}
                  />
                </div>

                {/* Performance Chart */}
                {examResults.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <Card>
                      <h3 className="text-lg font-semibold mb-4">Grade Distribution</h3>
                      <Chart type="doughnut" data={getPerformanceChartData()} options={chartOptions} />
                    </Card>
                    
                    <Card>
                      <h3 className="text-lg font-semibold mb-4">Performance Statistics</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span>Average Score</span>
                          <span className="font-semibold">
                            {examResults.length > 0 ? 
                              (examResults.reduce((sum, r) => sum + r.marks_obtained, 0) / examResults.length).toFixed(1) : 0
                            }%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Pass Rate</span>
                          <span className="font-semibold">
                            {examResults.length > 0 ? 
                              ((examResults.filter(r => r.marks_obtained >= selectedExam.passing_marks).length / examResults.length) * 100).toFixed(1) : 0
                            }%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Highest Score</span>
                          <span className="font-semibold">
                            {examResults.length > 0 ? Math.max(...examResults.map(r => r.marks_obtained)) : 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Lowest Score</span>
                          <span className="font-semibold">
                            {examResults.length > 0 ? Math.min(...examResults.map(r => r.marks_obtained)) : 0}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Results Table */}
                <DataTable value={examResults} responsiveLayout="scroll">
                  <Column field="roll_number" header="Roll No." sortable />
                  <Column 
                    field="student_name" 
                    header="Student Name"
                    sortable
                  />
                  <Column 
                    field="marks_obtained" 
                    header="Marks"
                    body={marksBodyTemplate}
                    sortable
                  />
                  <Column 
                    header="Percentage"
                    body={percentageBodyTemplate}
                  />
                  <Column 
                    field="grade" 
                    header="Grade"
                    body={(rowData) => getGradeBadge(rowData.grade)}
                  />
                  <Column 
                    field="result_status" 
                    header="Result"
                    body={(rowData) => (
                      <Badge 
                        value={rowData.marks_obtained >= selectedExam.passing_marks ? 'Pass' : 'Fail'} 
                        severity={rowData.marks_obtained >= selectedExam.passing_marks ? 'success' : 'danger'}
                      />
                    )}
                  />
                  <Column field="remarks" header="Remarks" />
                </DataTable>
              </Card>
            </div>
          ) : (
            <Card>
              <div className="text-center py-12 text-gray-500">
                <ChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select an exam from the "All Exams" tab to view results</p>
              </div>
            </Card>
          )}
        </TabPanel>

        <TabPanel header="Performance Analytics" leftIcon="pi pi-chart-line">
          <div className="space-y-6">
            {/* Overall Performance */}
            <Card>
              <h3 className="text-lg font-semibold mb-4">Overall Performance Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {exams.length > 0 ? 
                      (exams.reduce((sum, exam) => sum + (exam.average_score || 0), 0) / exams.length).toFixed(1) : 0
                    }%
                  </div>
                  <div className="text-gray-600">Average Score</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {exams.length > 0 ? 
                      (exams.reduce((sum, exam) => sum + (exam.pass_rate || 0), 0) / exams.length).toFixed(1) : 0
                    }%
                  </div>
                  <div className="text-gray-600">Average Pass Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {exams.reduce((sum, exam) => sum + (exam.total_students || 0), 0)}
                  </div>
                  <div className="text-gray-600">Total Students Examined</div>
                </div>
              </div>
            </Card>

            {/* Class-wise Performance */}
            <Card>
              <h3 className="text-lg font-semibold mb-4">Class-wise Performance</h3>
              <DataTable 
                value={classes.map(cls => {
                  const classExams = exams.filter(exam => exam.class_id === cls.id);
                  const avgScore = classExams.length > 0 ? 
                    (classExams.reduce((sum, exam) => sum + (exam.average_score || 0), 0) / classExams.length).toFixed(1) : 0;
                  const avgPassRate = classExams.length > 0 ? 
                    (classExams.reduce((sum, exam) => sum + (exam.pass_rate || 0), 0) / classExams.length).toFixed(1) : 0;
                  
                  return {
                    className: cls.name,
                    totalExams: classExams.length,
                    averageScore: avgScore,
                    averagePassRate: avgPassRate
                  };
                })}
                responsiveLayout="scroll"
              >
                <Column field="className" header="Class" />
                <Column field="totalExams" header="Total Exams" />
                <Column 
                  field="averageScore" 
                  header="Average Score"
                  body={(rowData) => `${rowData.averageScore}%`}
                />
                <Column 
                  field="averagePassRate" 
                  header="Average Pass Rate"
                  body={(rowData) => (
                    <div className="flex items-center space-x-2">
                      <ProgressBar value={parseFloat(rowData.averagePassRate)} className="w-24" />
                      <span>{rowData.averagePassRate}%</span>
                    </div>
                  )}
                />
              </DataTable>
            </Card>
          </div>
        </TabPanel>
      </TabView>

      {/* Exam Dialog */}
      <Dialog
        header={editingExam ? 'Edit Exam' : 'Create New Exam'}
        visible={examDialog}
        onHide={() => setExamDialog(false)}
        style={{ width: '600px' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setExamDialog(false)}
              className="p-button-text"
            />
            <Button
              label={editingExam ? 'Update' : 'Create'}
              icon="pi pi-check"
              onClick={handleSaveExam}
              loading={loading}
              className="p-button-success"
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exam Name *
              </label>
              <InputText
                value={examForm.name}
                onChange={(e) => setExamForm({ ...examForm, name: e.target.value })}
                placeholder="Enter exam name"
                className="w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exam Type *
              </label>
              <Dropdown
                value={examForm.examType}
                onChange={(e) => setExamForm({ ...examForm, examType: e.value })}
                options={examTypes}
                placeholder="Select exam type"
                className="w-full"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class *
              </label>
              <Dropdown
                value={classes.find(c => c.id === examForm.classId)}
                onChange={(e) => setExamForm({ ...examForm, classId: e.value?.id, sectionId: null })}
                options={classes}
                optionLabel="name"
                placeholder="Select a class"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section
              </label>
              <Dropdown
                value={sections.find(s => s.id === examForm.sectionId)}
                onChange={(e) => setExamForm({ ...examForm, sectionId: e.value?.id })}
                options={sections}
                optionLabel="name"
                placeholder="Select a section"
                className="w-full"
                disabled={!examForm.classId}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <Calendar
                value={examForm.startDate}
                onChange={(e) => setExamForm({ ...examForm, startDate: e.value })}
                showIcon
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <Calendar
                value={examForm.endDate}
                onChange={(e) => setExamForm({ ...examForm, endDate: e.value })}
                showIcon
                className="w-full"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Marks *
              </label>
              <InputNumber
                value={examForm.totalMarks}
                onValueChange={(e) => setExamForm({ ...examForm, totalMarks: e.value })}
                placeholder="Enter total marks"
                className="w-full"
                min={1}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passing Marks *
              </label>
              <InputNumber
                value={examForm.passingMarks}
                onValueChange={(e) => setExamForm({ ...examForm, passingMarks: e.value })}
                placeholder="Enter passing marks"
                className="w-full"
                min={1}
                max={examForm.totalMarks}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <InputTextarea
              value={examForm.description}
              onChange={(e) => setExamForm({ ...examForm, description: e.target.value })}
              placeholder="Enter exam description"
              rows={3}
              className="w-full"
            />
          </div>
          
          <div className="flex items-center">
            <Checkbox
              checked={examForm.isActive}
              onChange={(e) => setExamForm({ ...examForm, isActive: e.checked })}
              className="mr-2"
            />
            <label className="text-sm font-medium text-gray-700">
              Active Exam
            </label>
          </div>
        </div>
      </Dialog>

      {/* Result Dialog */}
      <Dialog
        header="Add Exam Result"
        visible={resultDialog}
        onHide={() => setResultDialog(false)}
        style={{ width: '500px' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setResultDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Add Result"
              icon="pi pi-check"
              onClick={handleSaveResult}
              loading={loading}
              className="p-button-success"
            />
          </div>
        }
      >
        {selectedExam && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold">{selectedExam.name}</h4>
              <p className="text-sm text-gray-600">
                {selectedExam.class_name} - {selectedExam.section_name}
              </p>
              <p className="text-sm">
                Total Marks: {selectedExam.total_marks} | Passing: {selectedExam.passing_marks}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Student *
              </label>
              <Dropdown
                value={students.find(s => s.id === resultForm.studentId)}
                onChange={(e) => setResultForm({ ...resultForm, studentId: e.value?.id })}
                options={students}
                optionLabel={(student) => `${student.first_name} ${student.last_name} (${student.roll_number})`}
                placeholder="Select a student"
                className="w-full"
                filter
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marks Obtained *
              </label>
              <InputNumber
                value={resultForm.marksObtained}
                onValueChange={(e) => setResultForm({ ...resultForm, marksObtained: e.value })}
                placeholder="Enter marks obtained"
                className="w-full"
                min={0}
                max={selectedExam.total_marks}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade *
              </label>
              <Dropdown
                value={resultForm.grade}
                onChange={(e) => setResultForm({ ...resultForm, grade: e.value })}
                options={gradeOptions}
                placeholder="Select grade"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <InputTextarea
                value={resultForm.remarks}
                onChange={(e) => setResultForm({ ...resultForm, remarks: e.target.value })}
                placeholder="Additional remarks"
                rows={3}
                className="w-full"
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Exams;